/**
 * ExamTourIntro.tsx
 * Intro dialog to start the product tour
 */

import { useTranslation } from 'react-i18next';
import { Play, X } from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@vsat/shared/components';

interface Props {
  isOpen: boolean;
  onStart: () => void;
  onSkip: () => void;
}

export default function ExamTourIntro({ isOpen, onStart, onSkip }: Props) {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={() => onSkip()}>
      <DialogContent className='sm:max-w-md rounded-2xl shadow-xl border border-border bg-white dark:bg-gray-800'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <div className='w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center'>
              <Play className='h-4 w-4 text-white' />
            </div>
            {t('tour.intro.title', i18n.language === 'vi' ? 'Hướng dẫn nhanh' : 'Quick Tour')}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            {t(
              'tour.intro.description',
              i18n.language === 'vi'
                ? 'Xem hướng dẫn 30 giây về các thao tác chính.'
                : 'Take a 30-second tour to learn key controls.'
            )}
          </p>

          <div className='flex gap-3'>
            <Button
              variant='outline'
              onClick={onSkip}
              className='flex-1 border border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
            >
              {t('tour.intro.skip', i18n.language === 'vi' ? 'Bỏ qua' : 'Skip')}
            </Button>
            <Button
              onClick={onStart}
              className='flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg shadow-primary/25 transition-all duration-300'
            >
              <Play className='h-4 w-4 mr-2' />
              {t('tour.intro.start', i18n.language === 'vi' ? 'Bắt đầu' : 'Start Tour')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
